import { useEffect, useState } from 'react';
import { API } from '../App';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { BookOpen, Users, Clock, Star, Crown, TrendingUp } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function CoursesMarketplace() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [courses, setCourses] = useState([]);
  const [featuredCourses, setFeaturedCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCourses();
    fetchFeaturedCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API}/courses?limit=50`);
      setCourses(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching courses:', error);
      setLoading(false);
    }
  };

  const fetchFeaturedCourses = async () => {
    try {
      const response = await axios.get(`${API}/courses/featured?limit=5`);
      setFeaturedCourses(response.data);
    } catch (error) {
      console.error('Error fetching featured courses:', error);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
              <BookOpen className="h-8 w-8 text-teal-500" />
              Course Marketplace
            </h1>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Learn new skills from experienced creators
            </p>
          </div>
        </div>

        {/* Featured Courses */}
        {featuredCourses.length > 0 && (
          <div>
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2 dark:text-white">
              <TrendingUp className="h-5 w-5 text-purple-500" />
              Featured Courses
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredCourses.map((course) => (
                <Card key={course.id} className="p-6 border-2 border-purple-200 dark:bg-gray-800 dark:border-purple-700 hover:shadow-xl transition-all">
                  <Badge className="bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300 mb-3">
                    ⭐ Featured
                  </Badge>
                  <h3 className="font-bold text-lg mb-2 dark:text-white">{course.title}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-4 line-clamp-2">
                    {course.description}
                  </p>
                  <div className="flex items-center justify-between text-sm mb-4">
                    <div className="flex items-center gap-1 text-gray-600 dark:text-gray-400">
                      <Users className="h-4 w-4" />
                      <span>{course.enrollment_count} students</span>
                    </div>
                    <Badge variant="secondary">{course.category}</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                      {course.price_gbp === 0 ? 'Free' : `£${course.price_gbp.toFixed(2)}`}
                    </span>
                    <Button
                      onClick={() => navigate(`/courses/${course.id}`)}
                      className="bg-purple-500 hover:bg-purple-600 text-white"
                    >
                      View Course
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* All Courses */}
        <div>
          <h2 className="text-xl font-bold mb-4 dark:text-white">All Courses</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {courses.map((course) => (
              <Card key={course.id} className="p-6 dark:bg-gray-800 hover:shadow-lg transition-shadow">
                <h3 className="font-bold text-lg mb-2 dark:text-white">{course.title}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-4 line-clamp-2">
                  {course.description}
                </p>
                
                <div className="flex items-center gap-4 text-sm mb-4">
                  <div className="flex items-center gap-1 text-gray-600 dark:text-gray-400">
                    <Users className="h-4 w-4" />
                    <span>{course.enrollment_count}</span>
                  </div>
                  {course.rating_avg > 0 && (
                    <div className="flex items-center gap-1">
                      <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />
                      <span>{course.rating_avg.toFixed(1)}</span>
                    </div>
                  )}
                  <Badge variant="secondary">{course.category}</Badge>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-2xl font-bold text-teal-600 dark:text-teal-400">
                    {course.price_gbp === 0 ? 'Free' : `£${course.price_gbp.toFixed(2)}`}
                  </span>
                  <Button
                    onClick={() => navigate(`/courses/${course.id}`)}
                    variant="outline"
                    className="border-teal-500 text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900"
                  >
                    View
                  </Button>
                </div>
              </Card>
            ))}
          </div>

          {courses.length === 0 && (
            <Card className="p-12 text-center dark:bg-gray-800">
              <BookOpen className="h-16 w-16 mx-auto mb-4 text-gray-400" />
              <p className="text-gray-500 dark:text-gray-400">No courses available yet</p>
            </Card>
          )}
        </div>
      </div>
    </Layout>
  );
}
